import type { ProjectCommitRecord } from '@/services/version-workspace';

/**
 * 版本历史图中跨轨道父提交连接。
 */
export interface VersionHistoryParentConnection {
  /** 父提交 ID，用于 UI 定位真实父提交所在行。 */
  readonly commitId: string;
  /** 父提交所在轨道。 */
  readonly laneIndex: number;
}

/**
 * 版本历史图中单条提交对应的渲染行。
 */
export interface VersionHistoryGraphRow {
  /** 提交 ID，用于让 UI 回查完整提交记录。 */
  readonly commitId: string;
  /** 分支 ID，用于保持轨道和颜色稳定。 */
  readonly branchId: string;
  /** 当前提交所在轨道，主线分支固定为 0。 */
  readonly laneIndex: number;
  /** 当前轨道颜色，供历史列表绘制节点和连线。 */
  readonly color: string;
  /** 与当前提交不在同一轨道的父提交轨道列表。 */
  readonly parentLaneIndexes: readonly number[];
  /** 与当前提交不在同一轨道的父提交连接列表。 */
  readonly parentConnections: readonly VersionHistoryParentConnection[];
}

/**
 * 版本历史图的轻量布局结果。
 */
export interface VersionHistoryGraph {
  /** 图中需要绘制的轨道总数。 */
  readonly laneCount: number;
  /** 与输入提交顺序一致的图行，调用方负责排序。 */
  readonly rows: readonly VersionHistoryGraphRow[];
}

/**
 * 分支轨道固定语义色池。
 *
 * 首色为主色占位，运行时通过 resolveBranchColors 替换为当前主题主色；
 * 其余颜色用于区分非主线分支，保持固定语义不随主题变化。
 */
export const BRANCH_COLORS = [
  '#2669dc',
  '#16a34a',
  '#dc2626',
  '#9333ea',
  '#ea580c',
  '#0891b2',
  '#4f46e5',
] as const;

/**
 * 按当前主题主色解析分支轨道颜色池。
 *
 * @param primaryColor 当前主题主色。
 * @returns 以主色为首色、其余保持固定语义色的颜色数组。
 */
export function resolveBranchColors(primaryColor: string): readonly string[] {
  return [primaryColor, ...BRANCH_COLORS.slice(1)];
}

/**
 * 判断提交是否属于默认主线分支。
 *
 * @param record 提交记录。
 * @returns 属于主线分支时返回 true。
 */
function isMainBranchRecord(record: ProjectCommitRecord): boolean {
  return record.branchId === 'main';
}

/**
 * 为提交记录构建稳定的分支轨道映射。
 *
 * 这里先完整扫描提交记录，是为了让父提交即使出现在当前行之后，也能被正确解析到它所属的轨道。
 *
 * @param records 调用方已排序的提交记录。
 * @returns 分支 ID 到轨道编号的映射。
 */
function buildBranchLaneMap(records: readonly ProjectCommitRecord[]): Map<string, number> {
  const branchLaneMap = new Map<string, number>();
  let nextLaneIndex = records.some(isMainBranchRecord) ? 1 : 0;

  for (const record of records) {
    if (branchLaneMap.has(record.branchId)) continue;

    if (isMainBranchRecord(record)) {
      branchLaneMap.set(record.branchId, 0);
      continue;
    }

    branchLaneMap.set(record.branchId, nextLaneIndex);
    nextLaneIndex += 1;
  }

  return branchLaneMap;
}

/**
 * 构建提交 ID 到轨道编号的快速查询表。
 *
 * @param records 调用方已排序的提交记录。
 * @param branchLaneMap 分支到轨道的稳定映射。
 * @returns 提交 ID 到轨道编号的映射。
 */
function buildCommitLaneMap(
  records: readonly ProjectCommitRecord[],
  branchLaneMap: ReadonlyMap<string, number>,
): Map<string, number> {
  const commitLaneMap = new Map<string, number>();

  for (const record of records) {
    const laneIndex = branchLaneMap.get(record.branchId);
    if (laneIndex === undefined) {
      throw new Error(`版本历史图缺少分支轨道：${record.branchId}`);
    }

    commitLaneMap.set(record.id, laneIndex);
  }

  return commitLaneMap;
}

/**
 * 获取提交跨轨道分叉来源连接。
 *
 * 当前版本记录没有合并概念，因此这里只读取第一个父提交作为“分支从哪里开始”的来源，
 * 不解析后续父提交，也不绘制多父提交合并关系。
 *
 * @param record 当前提交记录。
 * @param currentLaneIndex 当前提交所在轨道。
 * @param commitLaneMap 提交 ID 到轨道编号的映射。
 * @returns 去重后的跨轨道父提交连接列表。
 */
function collectParentConnections(
  record: ProjectCommitRecord,
  currentLaneIndex: number,
  commitLaneMap: ReadonlyMap<string, number>,
): VersionHistoryParentConnection[] {
  const parentId = record.parentIds[0];
  if (!parentId) return [];

  const parentLaneIndex = commitLaneMap.get(parentId);
  if (parentLaneIndex === undefined || parentLaneIndex === currentLaneIndex) return [];

  return [{
    commitId: parentId,
    laneIndex: parentLaneIndex,
  }];
}

/**
 * 从父提交连接列表提取去重后的父轨道编号。
 *
 * @param parentConnections 跨轨道父提交连接列表。
 * @returns 去重后的父轨道编号列表。
 */
function collectParentLaneIndexes(
  parentConnections: readonly VersionHistoryParentConnection[],
): number[] {
  const parentLaneIndexes: number[] = [];

  for (const parentConnection of parentConnections) {
    if (parentLaneIndexes.includes(parentConnection.laneIndex)) continue;
    parentLaneIndexes.push(parentConnection.laneIndex);
  }

  return parentLaneIndexes;
}

/**
 * 基于提交记录构建版本历史图。
 *
 * 此函数不排序、不请求接口、不维护 UI 状态；调用方传入什么顺序，返回行就保持什么顺序。
 * 当前仅支持侧栏所需的简化多分支轨道：主线固定 0，其他分支按首次出现顺序分配轨道。
 *
 * @param records 调用方已排序的项目提交记录。
 * @param branchColors 分支轨道颜色池，缺省使用 BRANCH_COLORS；UI 调用方应传入 resolveBranchColors(主色) 让首色跟随主题。
 * @returns 可供 UI 绘制的版本历史图。
 */
export function buildVersionHistoryGraph(
  records: readonly ProjectCommitRecord[],
  branchColors: readonly string[] = BRANCH_COLORS,
): VersionHistoryGraph {
  const branchLaneMap = buildBranchLaneMap(records);
  const commitLaneMap = buildCommitLaneMap(records, branchLaneMap);
  let maxLaneIndex = -1;
  const rows = records.map<VersionHistoryGraphRow>((record) => {
    const laneIndex = commitLaneMap.get(record.id);
    if (laneIndex === undefined) {
      throw new Error(`版本历史图缺少提交轨道：${record.id}`);
    }

    maxLaneIndex = Math.max(maxLaneIndex, laneIndex);

    const parentConnections = collectParentConnections(record, laneIndex, commitLaneMap);

    return {
      commitId: record.id,
      branchId: record.branchId,
      laneIndex,
      color: branchColors[laneIndex % branchColors.length] ?? '#64748b',
      parentLaneIndexes: collectParentLaneIndexes(parentConnections),
      parentConnections,
    };
  });

  return {
    laneCount: maxLaneIndex + 1,
    rows,
  };
}
